// ============================================================================
// HoneyTrap — DNC / suppression management UI.
// Layout: a compact 3-across action row on top, the DNC contacts list (full
// records, both channels) as the hero, and the raw suppression list (STOP replies
// + carrier blocks) collapsed below. Talks to /api/dnc, /api/suppression, /api/consent.
// ============================================================================
import { api } from '../lib/api.js'
import { esc } from '../lib/view.js'
import { fmtPhone, etDT } from '../lib/format.js'

const scopePill = (s) =>
  s === 'marketing' ? '<span class="pill mk">marketing</span>'
  : s === 'account_notification' ? '<span class="pill an">acct-notif</span>'
  : `<span class="pill">${esc(s)}</span>`

export function renderTrap(el) {
  el.innerHTML = `
    <div class="grid" style="gap:18px">
      <!-- COMPACT ACTIONS (3 across, wraps on narrow) -->
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:18px">
        <div class="card pad">
          <div class="mod-h">Add suppression</div>
          <div class="mod-sub">Manually suppress a number.</div>
          <div class="form-grid" style="margin-top:8px">
            <div class="field"><label>Phone</label><input class="h-input" id="aPhone" placeholder="(555) 123-4567" /></div>
            <div class="field"><label>Scope</label>
              <select class="h-input" id="aScope">
                <option value="marketing">Marketing only</option>
                <option value="all">All (incl. transactional)</option>
                <option value="account_notification">Account notification</option>
              </select>
            </div>
            <div class="field"><label>Reason</label>
              <select class="h-input" id="aReason">
                <option value="manual">Manual</option>
                <option value="dnc">DNC</option>
                <option value="complaint">Complaint</option>
              </select>
            </div>
            <div class="field"><label>&nbsp;</label><button class="btn" id="aAdd">Add</button></div>
          </div>
          <div class="msg" id="aMsg"></div>
        </div>

        <div class="card pad">
          <div class="mod-h">Partner DNC intake</div>
          <div class="mod-sub">Paste a partner DNC email — parse, suppress marketing on both channels, sync CIO. Or forward it to <b>dnc@honeytrapmail.com</b>.</div>
          <textarea class="h-input" id="dText" placeholder="Paste the DNC request email here…" style="margin-top:8px;min-height:92px"></textarea>
          <div style="margin-top:10px"><button class="btn" id="dGo">Parse &amp; suppress</button></div>
          <div class="msg" id="dMsg"></div>
        </div>

        <div class="card pad">
          <div class="mod-h">Consent lookup</div>
          <div class="mod-sub">The append-only consent ledger for a number (proof of opt-in / opt-out).</div>
          <div class="toolbar" style="margin-top:8px">
            <input class="h-input grow" id="cPhone" placeholder="Phone number…" />
            <button class="btn ghost" id="cGo">Look up</button>
          </div>
          <div id="cOut"></div>
        </div>
      </div>

      <!-- HERO: DNC CONTACTS (full records — email + phone) -->
      <div class="card pad">
        <div class="toolbar">
          <div class="grow">
            <div class="mod-h">DNC contacts</div>
            <div class="mod-sub">Every Do-Not-Contact request, full detail. Marketing is suppressed on <b>both</b> channels — SMS via the send gate, email via Customer.io.</div>
          </div>
          <button class="btn ghost" id="dcExport">Export CSV</button>
        </div>
        <div class="toolbar" style="margin-top:10px">
          <input class="h-input grow" id="dcSearch" placeholder="Search name, email, phone, company…" />
          <span class="mod-sub" id="dcCount" style="margin:0"></span>
        </div>
        <div class="table-wrap">
          <table class="h-table">
            <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Company</th><th>Suppressed</th><th>Source</th><th>Added</th><th></th></tr></thead>
            <tbody id="dcRows"><tr><td colspan="8" class="empty">Loading…</td></tr></tbody>
          </table>
        </div>
      </div>

      <!-- COLLAPSED: raw suppression list -->
      <div class="card pad">
        <div class="toolbar">
          <div class="grow">
            <div class="mod-h">All suppressed numbers</div>
            <div class="mod-sub">The raw enforcement list — includes STOP replies and carrier blocks (numbers with no name/email). Enforced on every send.</div>
          </div>
          <button class="btn ghost" id="supToggle">Show</button>
        </div>
        <div id="supBody" style="display:none;margin-top:12px">
          <div class="toolbar">
            <input class="h-input grow" id="tSearch" placeholder="Search by phone…" />
            <select class="h-input" id="tSize" style="width:auto">
              <option value="25">25 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
            </select>
            <button class="btn ghost" id="tExport">Export CSV</button>
          </div>
          <div class="table-wrap">
            <table class="h-table">
              <thead><tr><th>Phone</th><th>Scope</th><th>Reason</th><th>Provider</th><th>Added</th><th>CIO</th><th></th></tr></thead>
              <tbody id="tRows"><tr><td colspan="7" class="empty">Loading…</td></tr></tbody>
            </table>
          </div>
          <div class="toolbar" style="margin-top:12px;margin-bottom:0">
            <span class="mod-sub" id="tCount" style="margin:0"></span>
            <span class="grow"></span>
            <button class="btn ghost" id="tPrev">‹ Prev</button>
            <span class="mod-sub" id="tPage" style="margin:0"></span>
            <button class="btn ghost" id="tNext">Next ›</button>
          </div>
        </div>
      </div>
    </div>`

  const $ = (id) => el.querySelector('#' + id)

  // ---- DNC contacts (hero): full records + per-row Remove + Export ----
  async function loadDncContacts() {
    const rows = $('dcRows'), q = $('dcSearch').value.trim()
    rows.innerHTML = '<tr><td colspan="8" class="empty">Loading…</td></tr>'
    try {
      const r = await api.get('/api/dnc' + (q ? '?q=' + encodeURIComponent(q) : ''))
      const list = r.contacts || []
      $('dcCount').textContent = r.total != null ? `${r.shown} of ${r.total}` : ''
      rows.innerHTML = list.length ? list.map((c) => {
        const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || '—'
        const sms = c.phone ? '<span class="pill mk">SMS</span>' : ''
        const email = c.email
          ? (c.cio_status === 'synced'
              ? '<span class="pill an">Email</span>'
              : `<span class="pill" title="Customer.io: ${esc(c.cio_status || 'not flagged')}">Email*</span>`)
          : ''
        const badges = (sms || email) ? `<span style="display:inline-flex;gap:5px;white-space:nowrap">${sms}${email}</span>` : '—'
        const src = String(c.source || '—').replace(/^email:/, '✉ ').replace(/ \(backlog\)$/, '')
        return `<tr>
          <td style="white-space:nowrap">${esc(name)}</td>
          <td style="white-space:nowrap">${c.email ? esc(c.email) : '—'}</td>
          <td style="white-space:nowrap">${c.phone ? esc(fmtPhone(c.phone)) : '—'}</td>
          <td>${c.company ? esc(c.company) : '—'}</td>
          <td>${badges}</td>
          <td class="mod-sub" style="margin:0;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${esc(c.source || '')}">${esc(src)}</td>
          <td class="num" style="white-space:nowrap">${c.created_at ? esc(etDT(c.created_at)) : '—'}</td>
          <td><button class="btn ghost" data-email="${esc(c.email || '')}" data-phone="${esc(c.phone || '')}" data-name="${esc(name)}" style="padding:5px 12px">Remove</button></td>
        </tr>`
      }).join('') : '<tr><td colspan="8" class="empty">No DNC contacts yet.</td></tr>'
      rows.querySelectorAll('button[data-name]').forEach((b) =>
        b.addEventListener('click', () => removeDnc(b.dataset.email, b.dataset.phone, b.dataset.name)))
    } catch (e) { rows.innerHTML = `<tr><td colspan="8" class="empty">Error: ${esc(e.message)}</td></tr>` }
  }

  async function removeDnc(email, phone, name) {
    if (!confirm(`Remove ${name || email || fmtPhone(phone) || 'this contact'} from the DNC list? They can be marketed again (Customer.io unsubscribe is left as-is).`)) return
    try { await api.del('/api/dnc', { email: email || null, phone: phone || null }); loadDncContacts() }
    catch (e) { alert('Remove failed: ' + e.message) }
  }

  let dcT
  $('dcSearch').addEventListener('input', () => { clearTimeout(dcT); dcT = setTimeout(loadDncContacts, 250) })

  function dncCsv(list) {
    const cols = ['first_name', 'last_name', 'email', 'phone', 'company', 'source', 'cio_status', 'created_at']
    const cell = (r, c) => {
      let v = r[c] == null ? '' : String(r[c])
      if (c === 'created_at' && r[c]) v = new Date(r[c]).toISOString()
      if (/^[=+\-@\t\r]/.test(v)) v = "'" + v
      return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v
    }
    return [cols.join(','), ...list.map((r) => cols.map((c) => cell(r, c)).join(','))].join('\n')
  }
  $('dcExport').addEventListener('click', async () => {
    const btn = $('dcExport'), label = btn.textContent
    btn.textContent = 'Exporting…'; btn.disabled = true
    try {
      const r = await api.get('/api/dnc?limit=100000')
      download(dncCsv(r.contacts || []), `hive-dnc-contacts-${new Date().toISOString().slice(0, 10)}.csv`)
    } catch (e) { alert('Export failed: ' + e.message) }
    finally { btn.textContent = label; btn.disabled = false }
  })

  // ---- Raw suppression list (collapsed; lazy-loaded on first expand) ----
  let pageSize = 25, page = 0, total = 0, supLoaded = false

  $('supToggle').addEventListener('click', () => {
    const body = $('supBody'), open = body.style.display !== 'none'
    body.style.display = open ? 'none' : 'block'
    $('supToggle').textContent = open ? 'Show' : 'Hide'
    if (!open && !supLoaded) { supLoaded = true; loadSuppression() }
  })

  async function loadSuppression() {
    const rows = $('tRows'), q = $('tSearch').value.trim()
    rows.innerHTML = '<tr><td colspan="7" class="empty">Loading…</td></tr>'
    try {
      const r = await api.get(`/api/suppression?limit=${pageSize}&offset=${page * pageSize}${q ? '&q=' + encodeURIComponent(q) : ''}`)
      const list = r.suppression || []
      total = r.total || 0
      rows.innerHTML = list.length ? list.map((s) => `
        <tr>
          <td>${esc(fmtPhone(s.phone))}</td>
          <td>${scopePill(s.scope)}</td>
          <td>${esc(s.reason)}</td>
          <td>${esc(s.provider || '—')}</td>
          <td class="num">${s.created_at ? esc(etDT(s.created_at)) : '—'}</td>
          <td>${s.synced_cio ? '<span class="pill an">synced</span>' : '<span class="pill">—</span>'}</td>
          <td><button class="btn ghost" data-phone="${esc(s.phone)}" data-scope="${esc(s.scope)}" style="padding:5px 12px">Remove</button></td>
        </tr>`).join('') : '<tr><td colspan="7" class="empty">No suppressions found.</td></tr>'
      rows.querySelectorAll('button[data-phone]').forEach((b) => b.addEventListener('click', () => remove(b.dataset.phone, b.dataset.scope)))
      updatePager(list.length)
    } catch (e) { rows.innerHTML = `<tr><td colspan="7" class="empty">Error: ${esc(e.message)}</td></tr>` }
  }

  function updatePager(shown) {
    const from = total === 0 ? 0 : page * pageSize + 1
    const to = page * pageSize + shown
    const pages = Math.max(Math.ceil(total / pageSize), 1)
    $('tCount').textContent = `Showing ${from}–${to} of ${total}`
    $('tPage').textContent = `Page ${page + 1} of ${pages}`
    $('tPrev').disabled = page <= 0
    $('tNext').disabled = page >= pages - 1
    $('tPrev').style.opacity = page <= 0 ? '.5' : '1'
    $('tNext').style.opacity = page >= pages - 1 ? '.5' : '1'
  }

  async function remove(phone, scope) {
    if (!confirm(`Remove suppression for ${fmtPhone(phone)} (${scope})?`)) return
    try { await api.del('/api/suppression', { phone, scope }); loadSuppression() }
    catch (e) { alert('Remove failed: ' + e.message) }
  }

  function supCsv(list) {
    const cols = ['phone', 'scope', 'reason', 'provider', 'number', 'created_at', 'created_by', 'synced_cio']
    const cell = (r, c) => {
      let v = r[c] == null ? '' : String(r[c])
      if (c === 'created_at' && r[c]) v = new Date(r[c]).toISOString()
      if (/^[=+\-@\t\r]/.test(v)) v = "'" + v
      return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v
    }
    return [cols.join(','), ...list.map((r) => cols.map((c) => cell(r, c)).join(','))].join('\n')
  }
  $('tExport').addEventListener('click', async () => {
    const q = $('tSearch').value.trim()
    const btn = $('tExport'), label = btn.textContent
    btn.textContent = 'Exporting…'; btn.disabled = true
    try {
      const r = await api.get(`/api/suppression?limit=100000${q ? '&q=' + encodeURIComponent(q) : ''}`)
      download(supCsv(r.suppression || []), `hive-suppression-${new Date().toISOString().slice(0, 10)}.csv`)
    } catch (e) { alert('Export failed: ' + e.message) }
    finally { btn.textContent = label; btn.disabled = false }
  })

  $('tSize').addEventListener('change', () => { pageSize = parseInt($('tSize').value) || 25; page = 0; loadSuppression() })
  $('tPrev').addEventListener('click', () => { if (page > 0) { page--; loadSuppression() } })
  $('tNext').addEventListener('click', () => { page++; loadSuppression() })
  let t
  $('tSearch').addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => { page = 0; loadSuppression() }, 250) })

  // ---- shared CSV download ----
  function download(csv, filename) {
    const blob = new Blob([csv], { type: 'text/csv' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = filename
    document.body.appendChild(a); a.click(); a.remove()
    URL.revokeObjectURL(a.href)
  }

  // ---- Add suppression ----
  $('aAdd').addEventListener('click', async () => {
    const msg = $('aMsg'); msg.className = 'msg'; msg.textContent = ''
    const phone = $('aPhone').value.trim()
    if (!phone) { msg.className = 'msg bad'; msg.textContent = 'Enter a phone number.'; return }
    try {
      await api.post('/api/suppression', { phone, scope: $('aScope').value, reason: $('aReason').value })
      msg.className = 'msg ok'; msg.textContent = 'Added.'; $('aPhone').value = ''
      if (supLoaded) { page = 0; loadSuppression() }
    } catch (e) { msg.className = 'msg bad'; msg.textContent = e.message }
  })

  // ---- Partner DNC intake ----
  $('dGo').addEventListener('click', async () => {
    const msg = $('dMsg'); msg.className = 'msg'; msg.textContent = 'Working…'
    const text = $('dText').value.trim()
    if (!text) { msg.className = 'msg bad'; msg.textContent = 'Paste the DNC email first.'; return }
    try {
      const r = await api.post('/api/dnc', { text })
      const p = r.parsed || {}
      const cio = r.cio ? ` · CIO: ${esc(r.cio.status)}` : ''
      msg.className = 'msg ok'
      msg.innerHTML = `Suppressed ${esc(fmtPhone(p.phone) || p.email || 'contact')}${cio}`
      $('dText').value = ''
      loadDncContacts()
      if (supLoaded) { page = 0; loadSuppression() }
    } catch (e) { msg.className = 'msg bad'; msg.textContent = e.message }
  })

  // ---- Consent lookup ----
  $('cGo').addEventListener('click', async () => {
    const out = $('cOut'), phone = $('cPhone').value.trim()
    if (!phone) return
    out.innerHTML = '<div class="empty">Loading…</div>'
    try {
      const { consent = [] } = await api.get('/api/consent?phone=' + encodeURIComponent(phone))
      out.innerHTML = consent.length ? `
        <div class="table-wrap" style="margin-top:8px"><table class="h-table">
          <thead><tr><th>When</th><th>Status</th><th>Source</th></tr></thead>
          <tbody>${consent.map((c) => `
            <tr><td class="num" style="white-space:nowrap">${esc(etDT(c.captured_at))}</td>
                <td>${c.status === 'opt_out' ? '<span class="pill mk">opt-out</span>' : '<span class="pill an">opt-in</span>'}</td>
                <td class="mod-sub" style="margin:0">${esc(c.source || '—')}</td></tr>`).join('')}
          </tbody></table></div>` : '<div class="empty">No consent records for that number.</div>'
    } catch (e) { out.innerHTML = `<div class="msg bad">${esc(e.message)}</div>` }
  })

  loadDncContacts()
}
